import { onScopeDispose, toValue, type MaybeRefOrGetter } from "vue";
import type { Router } from "vue-router";
import type { ConfirmationOptions } from "primevue/confirmationoptions";
import { confirmModal } from "@/components/shared/form/ConfirmationModal";
import { t } from "@/language/translation";

type ConfirmationOptionsWithoutCallbacks = Omit<Partial<ConfirmationOptions>, "accept" | "reject">;

export type UnsavedChangesSource = {
  isDirty: MaybeRefOrGetter<boolean>;
  confirmationOptions?: ConfirmationOptionsWithoutCallbacks;
  onDiscard?: () => void | Promise<void>;
};

const sources = new Map<symbol, UnsavedChangesSource>();
let activeConfirmation: Promise<boolean> | undefined;
let activeNavigationConfirmation: Promise<boolean> | undefined;
let skipNextNavigation = false;

export function useUnsavedChanges(source: UnsavedChangesSource) {
  const sourceId = Symbol("unsaved-changes-source");
  sources.set(sourceId, source);
  onScopeDispose(() => sources.delete(sourceId));
}

export function hasUnsavedChanges() {
  return [...sources.values()].some((source) => toValue(source.isDirty));
}

export function confirmDiscardChanges(
  options: ConfirmationOptionsWithoutCallbacks = {},
): Promise<boolean> {
  if (import.meta.env.MODE === "test") {
    // eslint-disable-next-line no-console -- the Playwright fixture listens for this test-only signal
    console.error(`[E2E_UNEXPECTED_DISCARD_CHANGES] ${window.location.href}`);
  }

  if (activeConfirmation != null) {
    return activeConfirmation;
  }

  activeConfirmation = new Promise<boolean>((resolve) => {
    confirmModal({
      ...options,
      accept: () => resolve(true),
      reject: () => resolve(false),
      onHide: () => {
        options.onHide?.();
        resolve(false);
      },
    });
  }).finally(() => {
    activeConfirmation = undefined;
  });

  return activeConfirmation;
}

export function allowNextNavigation() {
  skipNextNavigation = true;
}

async function confirmRegisteredUnsavedChanges() {
  if (skipNextNavigation) {
    skipNextNavigation = false;
    return true;
  }

  const dirtySources = [...sources.values()].filter((source) => toValue(source.isDirty));
  if (dirtySources.length === 0) {
    return true;
  }

  if (activeNavigationConfirmation != null) {
    return activeNavigationConfirmation;
  }

  activeNavigationConfirmation = (async () => {
    const accepted = await confirmDiscardChanges({
      ...dirtySources[0]?.confirmationOptions,
      acceptLabel: t("global.leave"),
      rejectLabel: t("global.cancel"),
    });
    if (!accepted) {
      return false;
    }

    await Promise.all(dirtySources.map((source) => source.onDiscard?.()));
    return true;
  })().finally(() => {
    activeNavigationConfirmation = undefined;
  });

  return activeNavigationConfirmation;
}

export function installUnsavedChangesNavigationGuard(router: Router) {
  router.beforeEach(() => confirmRegisteredUnsavedChanges());

  if (document.location.hostname !== "localhost") {
    window.addEventListener("beforeunload", (event) => {
      if (!hasUnsavedChanges()) {
        return;
      }

      event.preventDefault();
      event.returnValue = "";
    });
  }
}
